import { memo } from 'react';
import { Avatar, Badge, Tooltip, Box } from '@mui/material';
import { WifiOff as OfflineIcon } from '@mui/icons-material';
import { renderIcon } from '../IconPicker';

export interface PlayerAvatarProps {
  displayName: string;
  handle?: string;
  iconName?: string;
  isOffline?: boolean;
  showBadge?: boolean;
  size?: 'small' | 'medium' | 'large';
  roleIndicator?: 'owner' | 'admin' | null;
  disableTooltip?: boolean;
}

const sizeMap = {
  small: 24,
  medium: 40,
  large: 56,
};

/**
 * Reusable player avatar with optional offline indicator and role badge
 */
export const PlayerAvatar = memo<PlayerAvatarProps>(({
  displayName,
  handle,
  iconName,
  isOffline = false,
  showBadge = true,
  size = 'medium',
  roleIndicator,
  disableTooltip = false,
}) => {
  const avatarSize = sizeMap[size];
  const initial = displayName?.charAt(0) || '?';

  const avatar = (
    <Avatar
      sx={{
        width: avatarSize,
        height: avatarSize,
        bgcolor: isOffline ? 'grey.400' : 'primary.main',
        fontSize: size === 'small' ? 12 : size === 'large' ? 24 : 16,
      }}
    >
      {iconName ? renderIcon(iconName, avatarSize * 0.6) : initial}
    </Avatar>
  );

  const wrappedAvatar = showBadge ? (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        isOffline ? (
          <OfflineIcon sx={{ fontSize: 12, color: 'grey.500' }} />
        ) : roleIndicator ? (
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: roleIndicator === 'owner' ? 'warning.main' : 'info.main',
              border: '2px solid white',
            }}
          />
        ) : null
      }
    >
      {avatar}
    </Badge>
  ) : (
    avatar
  );

  const tooltipContent = [
    displayName,
    handle && `@${handle}`,
    isOffline && '(Offline Player)',
    roleIndicator === 'owner' && '(Owner)',
    roleIndicator === 'admin' && '(Admin)',
  ]
    .filter(Boolean)
    .join(' ');

  if (disableTooltip) {
    return wrappedAvatar;
  }

  return (
    <Tooltip title={tooltipContent}>
      {wrappedAvatar}
    </Tooltip>
  );
});

PlayerAvatar.displayName = 'PlayerAvatar';

export default PlayerAvatar;
